import { collection, getDocs } from "firebase/firestore";
import { useState, useEffect, useRef } from "react";
import { db } from "../../firebase";
import * as XLSX from "xlsx";
import { FiChevronDown, FiSearch, FiDownload } from "react-icons/fi";
import PaymentsFilters from "./PaymentsFilters";
import PaymentsTable from "./PaymentsTable";
import { getTodayIST } from "../../utils/dateUtils";

export default function PaymentsSection() {
  const [filterStatus, setFilterStatus] = useState([]); // unpaid, paid, package
  const [filterBranch, setFilterBranch] = useState([]); // all branches
  const [patients, setPatients] = useState([]);
  const [packages, setPackages] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [searchText, setSearchText] = useState(""); // search box
  const [filterDuration, setFilterDuration] = useState("today"); // default today
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [dateError, setDateError] = useState("");
  const [loading, setLoading] = useState(false);
  const getDateRange = () => {
    const today = getTodayIST();

    let fromDate = today;
    let toDate = today;

    // ✅ custom range (highest priority)
    if (startDate && endDate) {
      fromDate = startDate;
      toDate = endDate;

      if (toDate < fromDate) {
        [fromDate, toDate] = [toDate, fromDate];
      }
    }

    // ✅ only start date
    else if (startDate) {
      fromDate = startDate;
      toDate = startDate;
    }

    // ✅ duration filters (SAME as package queue)
    else if (filterDuration === "yesterday") {
      const d = new Date(today);
      d.setDate(d.getDate() - 1);
      fromDate = d.toISOString().split("T")[0];
      toDate = fromDate;
    } else if (filterDuration === "7") {
      const d = new Date(today);
      d.setDate(d.getDate() - 6);
      fromDate = d.toISOString().split("T")[0];
      toDate = today;
    } else if (filterDuration === "30") {
      const d = new Date(today);
      d.setDate(d.getDate() - 29);
      fromDate = d.toISOString().split("T")[0];
      toDate = today;
    } else if (filterDuration === "60") {
      const d = new Date(today);
      d.setDate(d.getDate() - 59);
      fromDate = d.toISOString().split("T")[0];
      toDate = today;
    }

    return { fromDate, toDate };
  };
 const getDateOnly = (timestamp) => {
  if (!timestamp?.seconds) return null;

  const d = new Date(timestamp.seconds * 1000);

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
};


  const formatToYYYYMMDD = (date) => {
    if (!date) return null;

    // Firestore timestamp
    if (date.seconds) {
      const d = new Date(date.seconds * 1000);
      return d.toISOString().split("T")[0];
    }

    // dd/mm/yyyy format
    if (typeof date === "string" && date.includes("/")) {
      const [dd, mm, yyyy] = date.split("/");
      return `${yyyy}-${mm}-${dd}`;
    }

    // already yyyy-mm-dd
    return date;
  };
  useEffect(() => {
    // Do nothing on page load
  }, []);
  const handleVPJCClick = (vpjcId) => {
    const url = `/admin-dashboard?tab=patients&vpjcId=${vpjcId}`;
    window.location.href = url;
  };

  const fetchData = async () => {
    console.log("Filter Branch:", filterBranch);
  console.log("Filter Status:", filterStatus);
  console.log("Search Text:", searchText);

    const { fromDate, toDate } = getDateRange();
  try {
    setLoading(true);

    // =========================
    // ✅ TODAY RANGE (EOD LOGIC)
    // =========================
const startOfDay = new Date(fromDate);
startOfDay.setHours(0, 0, 0, 0);

const endOfDay = new Date(toDate);
endOfDay.setHours(23, 59, 59, 999);

    // =========================
    // 🔹 FETCH ALL COLLECTIONS
    // =========================
    const [visitSnap, treatmentSnap, patientSnap, pkgPaySnap, pkgSnap]  =
      await Promise.all([
        getDocs(collection(db, "visits")),
        getDocs(collection(db, "treatment_entries")),
        getDocs(collection(db, "patients")),
        getDocs(collection(db, "package_payments")),
        getDocs(collection(db, "packages")),
      ]);

    // =========================
    // 🔹 PATIENT MAP
    // =========================
    const patientMap = new Map();
    patientSnap.docs.forEach((doc) => {
      patientMap.set(doc.id, doc.data());
    });

    // =========================
    // ✅ 1. CONSULTATION + UNPAID TREATMENT (VISITS)
    // =========================
    const visitData = visitSnap.docs
      .map((doc) => {
        const d = doc.data();

        const visitDate = d.created_at?.toDate
          ? d.created_at.toDate()
          : new Date(d.created_at);

        if (!visitDate || visitDate < startOfDay || visitDate > endOfDay)
          return null;

        // 🔹 Branch filter
        const normalize = (b) =>
          b?.toLowerCase().replace(" branch", "").trim();

        if (
          filterBranch.length > 0 &&
          !filterBranch.map(normalize).includes(normalize(d.branch_name))
        ) {
          return null;
        }

        // =========================
        // CONSULTATION
        // =========================
        if (d.visit_category === "CONSULTATION") {
          const isPaid = d.consultation_payment_status === "PAID";

          return {
            vpjc_id: d.vpjc_id,
            name: d.name,
            mobile: d.mobile,
            branch_name: d.branch_name,
            payment_amount: isPaid ? Number(d.consultation_fee || 0) : 0,
            payment_status: isPaid ? "PAID" : "UNPAID",
            type: "CONSULTATION",
            created_at: d.created_at,
          };
        }

        // =========================
        // UNPAID TREATMENT ONLY
        // =========================
        if (d.visit_category === "TREATMENT") {
          const isPaid = d.treatment_payment_status === "PAID";

          if (!isPaid) {
            return {
              vpjc_id: d.vpjc_id,
              name: d.name,
              mobile: d.mobile,
              branch_name: d.branch_name,
              payment_amount: 0,
              payment_status: "UNPAID",
              type: "TREATMENT",
              created_at: d.created_at,
            };
          }
        }

        return null;
      })
      .filter(Boolean);

    // =========================
    // ✅ 2. PAID TREATMENTS (treatment_entries)
    // =========================
    const treatments = treatmentSnap.docs
      .map((doc) => {
        const d = doc.data();

        const trDate = d.created_at?.toDate
          ? d.created_at.toDate()
          : new Date(d.created_at);

        if (!trDate || trDate < startOfDay || trDate > endOfDay)
          return null;

        const normalize = (b) =>
          b?.toLowerCase().replace(" branch", "").trim();

        if (
          filterBranch.length > 0 &&
          !filterBranch.map(normalize).includes(normalize(d.branch_name))
        ) {
          return null;
        }

        const p = patientMap.get(d.patient_id);

        return {
          vpjc_id: p?.vpjc_id || "-",
          name: p?.name || "-",
          mobile: p?.mobile || "-",
          branch_name: d.branch_name,
          payment_amount: Number(d.amount || 0),
          payment_status: "PAID",
          type: "TREATMENT",
          created_at: d.created_at,
        };
      })
      .filter(Boolean);

    // =========================
    // ✅ 3. PACKAGE PAYMENTS (PAID)
    // =========================
    const packagePayments = pkgPaySnap.docs
      .map((doc) => {
        const d = doc.data();

        const pkgDate = d.created_at?.toDate
          ? d.created_at.toDate()
          : new Date(d.created_at);

        if (!pkgDate || pkgDate < startOfDay || pkgDate > endOfDay)
          return null;

        const normalize = (b) =>
          b?.toLowerCase().replace(" branch", "").trim();

        if (
          filterBranch.length > 0 &&
          !filterBranch.map(normalize).includes(normalize(d.branch_name))
        ) {
          return null;
        }

        const p = patientMap.get(d.patient_id);

        return {
          vpjc_id: p?.vpjc_id || d.vpjc_id,
          name: p?.name || "-",
          mobile: p?.mobile || "-",
          branch_name: d.branch_name,
          payment_amount: Number(d.amount || 0),
          payment_status: "PAID",
          type: "PACKAGE",
          created_at: d.created_at,
        };
      })
      .filter(Boolean);

      // =========================
// ✅ 4. PACKAGE UNPAID (DUE)
// =========================
const packageDue = pkgSnap.docs
  .map((doc) => {
    const d = doc.data();

    if (!d.start_date) return null;

    const pkgDate = d.start_date?.toDate
      ? d.start_date.toDate()
      : new Date(d.start_date);

    if (!pkgDate || pkgDate < startOfDay || pkgDate > endOfDay)
      return null;

    // ✅ ADD THIS
    const normalize = (b) =>
      b?.toLowerCase().replace(" branch", "").trim();

    const selectedBranches = filterBranch.map(normalize);
    const currentBranch = normalize(d.branch_name);

    if (
      filterBranch.length > 0 &&
      !selectedBranches.includes(currentBranch)
    ) {
      return null;
    }

    const due = Number(d.due_amount || 0);
    const paid = Number(d.paid_amount || 0);

   if (due <= 0) return null;

    const p = patientMap.get(d.patient_id);

    return {
      vpjc_id: d.vpjc_id,
      name: p?.name || "-",
      mobile: p?.mobile || "-",
      branch_name: d.branch_name,
      payment_amount: due,
      payment_status: "UNPAID",
      type: "PACKAGE",
      created_at: d.start_date,
    };
  })
  .filter(Boolean);
  

    // =========================
    // ✅ FINAL MERGE
    // =========================
    let finalData = [
      ...visitData,
      ...treatments,
      ...packagePayments,
      ...packageDue,
    ];

    // =========================
    // ✅ STATUS FILTER
    // =========================
    const isPaid = filterStatus.includes("paid");
    const isUnpaid = filterStatus.includes("unpaid");

    if (isPaid && !isUnpaid) {
      finalData = finalData.filter((p) => p.payment_status === "PAID");
    } else if (isUnpaid && !isPaid) {
      finalData = finalData.filter((p) => p.payment_status === "UNPAID");
    }

    // =========================
    // ✅ TYPE FILTER
    // =========================
    const hasTypeFilter =
      filterStatus.includes("consultation") ||
      filterStatus.includes("treatment") ||
      filterStatus.includes("package");

    if (hasTypeFilter) {
      const selectedTypes = [];

      if (filterStatus.includes("consultation")) selectedTypes.push("CONSULTATION");
      if (filterStatus.includes("treatment")) selectedTypes.push("TREATMENT");
      if (filterStatus.includes("package")) selectedTypes.push("PACKAGE");

      finalData = finalData.filter((p) =>
        selectedTypes.includes(p.type)
      );
    } else {
      // default: hide packages unless selected
      // finalData = finalData.filter((p) => p.type !== "PACKAGE");
    }

    // =========================
    // ✅ SEARCH
    // =========================
    if (searchText?.trim()) {
  const s = searchText.replace(/\s+/g, "").toLowerCase();

  finalData = finalData.filter((p) => {
    const vpjc = p.vpjc_id?.replace(/\s+/g, "").toLowerCase() || "";
    const name = p.name?.toLowerCase() || "";
    const mobile = p.mobile?.toString() || "";

    return (
      vpjc.includes(s) ||
      name.includes(s) ||
      mobile.includes(s)
    );
  });
}

    // =========================
    // ✅ SORT (LATEST FIRST)
    // =========================
    finalData.sort((a, b) => {
      const t1 = a.created_at?.seconds
        ? a.created_at.seconds * 1000
        : new Date(a.created_at).getTime();

      const t2 = b.created_at?.seconds
        ? b.created_at.seconds * 1000
        : new Date(b.created_at).getTime();

      return t2 - t1;
    });

    // =========================
    // ✅ TOTAL (ONLY PAID)
    // =========================
    const total = finalData.reduce((sum, p) => {
      return p.payment_status === "PAID"
        ? sum + Number(p.payment_amount || 0)
        : sum;
    }, 0);

    // =========================
    // ✅ SET STATE
    // =========================
    setPatients(finalData);
    setTotalAmount(total);
    setHasSearched(true);

  } catch (err) {
    console.error(err);
  } finally {
    setLoading(false);
  }
};

  // Format date as dd-mm-yyyy
  const formatDate = (date) => {
    if (!date) return "-";
    if (date.seconds) date = new Date(date.seconds * 1000);
    else date = new Date(date);
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };
  const exportToExcel = () => {
    let data = [];

    if (filterStatus.includes("package")) {
      data = packages.map((p) => ({
        VPJC_ID: p.vpjc_id,
        Name: p.name,
        Mobile: p.mobile,
        Total_Amount: p.total_amount,
        Paid_Amount: p.paid_amount,
        Due_Amount: p.due_amount,
        Status: p.status,
        Branch: p.branch_name,
        Start_Date: formatDate(p.start_date),
        Expiry_Date: formatDate(p.expiry_date),
      }));
    } else {
      data = [...patients]
        .sort((a, b) => {
          const d1 = a.visit_date?.seconds || 0;
          const d2 = b.visit_date?.seconds || 0;
          return d2 - d1;
        })
        .map((p) => ({
          VPJC_ID: p.vpjc_id,
          Name: p.name,
          Mobile: p.mobile,
          Amount: p.payment_amount,
          Status: p.payment_status,
          Branch: p.branch_name,
          Visit_Date: formatDate(p.visit_date),
        }));

      // ADD TOTAL AMOUNT ROW
      if (filterStatus.includes("paid")) {
        data.push({});
        data.push({
          Name: "TOTAL PAID AMOUNT",
          Amount: totalAmount,
        });
      }
    }
    data.sort((a, b) => {
      if (!a.Visit_Date) return 1;
      if (!b.Visit_Date) return -1;

      const d1 = new Date(a.Visit_Date.split("/").reverse().join("-"));
      const d2 = new Date(b.Visit_Date.split("/").reverse().join("-"));

      return d2 - d1;
    });
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payments");

    // FILE NAME LOGIC
    // FILE NAME LOGIC
    let fileLabel = "";

    // If specific date selected
    if (startDate && endDate) {
      fileLabel = `${startDate}_to_${endDate}`;
    }

    // Duration options
    else {
      switch (filterDuration) {
        case "today":
          fileLabel = "Today";
          break;

        case "yesterday":
          fileLabel = "Yesterday";
          break;

        case "1_week":
          fileLabel = "Last_1_Week";

          break;
        case "30_days":
          fileLabel = "Last_30_Days";
          break;

        case "60_days":
          fileLabel = "Last_60_Days";
          break;
        default:
          fileLabel = "Duration";
      }
    }

    let statusLabel =
      filterStatus.length > 0 ? filterStatus.join("_") : "AllStatus";

    let branchLabel =
      filterBranch.length > 0 ? filterBranch.join("_") : "AllBranches";

    let fileName = `Payments_${statusLabel}_${branchLabel}_${fileLabel}.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };
  const formatStatus = (status) => status?.toUpperCase() || "-";
  const statusColor = (status) => {
    const s = status?.toUpperCase();
    return s === "PAID" || s === "ACTIVE" ? "text-green-600" : "text-red-600";
  };

  const showBranchColumn = filterBranch.length === 0;

  return (
    <div className="p-4 md:p-6 bg-gray-100 min-h-screen">
      <h2 className="text-2xl font-bold mb-4 text-[#204080]">Payments</h2>
      <PaymentsFilters
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        filterBranch={filterBranch}
        setFilterBranch={setFilterBranch}
        filterDuration={filterDuration}
        setFilterDuration={setFilterDuration}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        searchText={searchText}
        setSearchText={setSearchText}
        fetchData={fetchData}
        exportToExcel={exportToExcel}
        totalAmount={totalAmount}
        patients={patients}
        setPatients={setPatients}
        setPackages={setPackages}
        setTotalAmount={setTotalAmount}
        setHasSearched={setHasSearched}
        setDateError={setDateError}
        dateError={dateError}
      />

      <PaymentsTable
        patients={patients}
        packages={packages}
        filterStatus={filterStatus}
        showBranchColumn={showBranchColumn}
        handleVPJCClick={handleVPJCClick}
        formatStatus={formatStatus}
        statusColor={statusColor}
        formatDate={formatDate}
        hasSearched={hasSearched}
        loading={loading}
      />
    </div>
  );
}
